import React, { useState, useEffect } from 'react';
import Layout from '../components/layouts/Main';
import { Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';

const Dashboard = () => {
  const [statsData, setStatsData] = useState({
    totalPackages: 0,
    totalClients: 0,
    activeSubscriptions: 0,
    totalRevenue: 0,
    recentClients: [],
    recentSubscriptions: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${config.API_BASE_URL}/api/master/dashboard/stats`);
        setStatsData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const stats = [
    {
      title: 'Total Packages',
      count: statsData.totalPackages,
      icon: 'package',
      link: '/packages',
      color: 'primary'
    },
    {
      title: 'Total Clients',
      count: statsData.totalClients,
      icon: 'users',
      link: '/clients',
      color: 'success'
    },
    {
      title: 'Active Subscriptions',
      count: statsData.activeSubscriptions,
      icon: 'check-circle',
      link: '/clients',
      color: 'info'
    },
    {
      title: 'Total Revenue',
      count: `₹${(statsData.totalRevenue || 0).toLocaleString()}`,
      icon: 'rupee-sign',
      link: '/packages',
      color: 'warning'
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="dashboard">
        <nav className="mb-3" aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item">
              <a href="#!">Pages</a>
            </li>
            <li className="breadcrumb-item active">Dashboard</li>
          </ol>
        </nav>

        <h2 className="text-bold text-body-emphasis mb-5">Dashboard</h2>

        {/* Stats Cards */}
        <div className="row g-3 mb-5">
          {stats.map((stat, index) => (
            <div key={index} className="col-md-6 col-xl-3">
              <div className="card">
                <div className="card-body">
                  <div className="d-flex align-items-center">
                    <div className={`avatar avatar-xl me-3 bg-${stat.color}-subtle`}>
                      <i className={`fas fa-${stat.icon} fs-3 text-${stat.color}`}></i>
                    </div>
                    <div className="flex-1">
                      <h5 className="text-body-emphasis mb-1">{stat.count}</h5>
                      <p className="text-body-tertiary mb-0">{stat.title}</p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <Link to={stat.link} className="btn btn-sm btn-outline-primary">
                      View Details
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="row g-3 mb-5">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h5 className="mb-0">Quick Actions</h5>
              </div>
              <div className="card-body">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <Link to="/packages" className="btn btn-primary w-100 d-flex align-items-center justify-content-center">
                      <i className="fas fa-plus me-2"></i>
                      Create Package
                    </Link>
                  </div>
                  <div className="col-md-6 col-lg-3">
                    <Link to="/clients" className="btn btn-success w-100 d-flex align-items-center justify-content-center">
                      <i className="fas fa-user-plus me-2"></i>
                      Add Client
                    </Link>
                  </div>
                  <div className="col-md-6 col-lg-3">
                    <Link to="/clients" className="btn btn-info w-100 d-flex align-items-center justify-content-center">
                      <i className="fas fa-user-tag me-2"></i>
                      Manage Subscriptions
                    </Link>
                  </div>
                  <div className="col-md-6 col-lg-3">
                    <Link to="/packages" className="btn btn-warning w-100 d-flex align-items-center justify-content-center">
                      <i className="fas fa-edit me-2"></i>
                      Edit Packages
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="row g-3">
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Clients</h5>
                <Link to="/clients" className="btn btn-sm btn-link">View All</Link>
              </div>
              <div className="card-body p-0">
                {!statsData.recentClients || statsData.recentClients.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No recent clients</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm fs-9 mb-0">
                      <thead>
                        <tr>
                          <th className="px-3">Company</th>
                          <th>Status</th>
                          <th className="text-end px-3">Joined</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsData.recentClients.map(client => (
                          <tr key={client._id}>
                            <td className="px-3">
                              <Link to={`/clients/${client._id}`} className="fw-semibold text-body-highlight">
                                {client.companyName}
                              </Link>
                            </td>
                            <td>
                              <span className={`badge ${client.status === 'active' ? 'bg-success' : 'bg-secondary'}`}>
                                {client.status}
                              </span>
                            </td>
                            <td className="text-end px-3 text-body-tertiary">
                              {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card h-100">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Subscriptions</h5>
                <Link to="/clients" className="btn btn-sm btn-link">View All</Link>
              </div>
              <div className="card-body p-0">
                {!statsData.recentSubscriptions || statsData.recentSubscriptions.length === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-0">No recent subscriptions</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm fs-9 mb-0">
                      <thead>
                        <tr>
                          <th className="px-3">Client</th>
                          <th>Package</th>
                          <th className="text-end px-3">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statsData.recentSubscriptions.map(sub => (
                          <tr key={sub._id}>
                            <td className="px-3">{sub.clientId?.companyName || 'N/A'}</td>
                            <td>{sub.packageId?.name || 'N/A'}</td>
                            <td className="text-end px-3 fw-bold text-success">
                              ₹{(sub.paymentDetails?.amount || 0).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
